<script setup>
import { ref, reactive, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { isValidDomain } from '@chatwoot/utils';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  customDomain: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['addCustomDomain', 'close']);

const { t } = useI18n();

const isMounted = ref(true);
onBeforeUnmount(() => {
  isMounted.value = false;
});

const dialogRef = ref(null);

const formState = reactive({
  customDomain: props.customDomain,
});

const rules = {
  customDomain: {
    isValidDomain: helpers.withMessage(
      () => t('INBOX_MGMT.CSAT.CUSTOM_DOMAIN.DIALOG.FORMAT_ERROR'),
      isValidDomain
    ),
  },
};

const v$ = useVuelidate(rules, formState);

watch(
  () => props.customDomain,
  newVal => {
    if (isMounted.value) {
      formState.customDomain = newVal;
    }
  }
);

const handleDialogConfirm = async () => {
  if (!isMounted.value) return;
  if (!(await v$.value.$validate())) return;

  emit('addCustomDomain', formState.customDomain);
};

const handleClose = () => {
  if (!isMounted.value) return;
  v$.value.$reset();
  emit('close');
};

defineExpose({
  dialogRef,
  close: () => dialogRef.value?.close(),
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      t(
        `INBOX_MGMT.CSAT.CUSTOM_DOMAIN.DIALOG.${props.mode.toUpperCase()}_HEADER`
      )
    "
    :confirm-button-label="
      t(
        `INBOX_MGMT.CSAT.CUSTOM_DOMAIN.DIALOG.${props.mode.toUpperCase()}_CONFIRM_BUTTON_LABEL`
      )
    "
    @confirm="handleDialogConfirm"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="formState.customDomain"
        :label="t('INBOX_MGMT.CSAT.CUSTOM_DOMAIN.DIALOG.LABEL')"
        :placeholder="t('INBOX_MGMT.CSAT.CUSTOM_DOMAIN.DIALOG.PLACEHOLDER')"
        :message="
          v$.customDomain.$error ? v$.customDomain.$errors[0].$message : ''
        "
        :message-type="v$.customDomain.$error ? 'error' : 'info'"
        @blur="v$.customDomain.$touch()"
      />
    </div>
  </Dialog>
</template>
